import { useState, useEffect, useCallback } from 'react';
import {
  supplyChainNodes as initialNodes,
  supplyChainEdges as initialEdges,
  trucks as initialTrucks,
  dashboardData as initialDashboard,
  orders,
  loadingSlots,
  planningData,
  weighBridge as initialWeighBridge,
  materialColors,
  // Re-export static helpers
  getOrderById,
  getTruckById,
  getNodeById,
  getOrdersForNode,
  getSlotForTruck,
  getTrucksForGate,
  getOrdersByStatus,
  orderStatuses,
  truckStatuses,
  orderStatusLabels,
  truckStatusLabels,
} from '@/data/mockData';

// Deep clone helper
const clone = (obj) => JSON.parse(JSON.stringify(obj));

export function useSimulation() {
  const [nodes, setNodes] = useState(() => clone(initialNodes));
  const [edges, setEdges] = useState(() => clone(initialEdges));
  const [trucks, setTrucks] = useState(() => clone(initialTrucks));
  const [dashboard, setDashboard] = useState(() => clone(initialDashboard));
  const [weighBridge, setWeighBridge] = useState(() => clone(initialWeighBridge));
  const [tick, setTick] = useState(0);

  // Randomize node outputs slightly (+/- 2%)
  const jitterNodes = useCallback(() => {
    setNodes(prev => prev.map(node => {
      const jitter = 1 + (Math.random() * 0.04 - 0.02);
      const newCurrent = Math.round(node.capacity.current * jitter);
      const clamped = Math.min(newCurrent, node.capacity.max);
      return {
        ...node,
        capacity: { ...node.capacity, current: clamped },
        utilization: Math.round((clamped / node.capacity.max) * 100),
      };
    }));

    setEdges(prev => prev.map(edge => {
      const jitter = 1 + (Math.random() * 0.04 - 0.02);
      return {
        ...edge,
        flowRate: Math.round(edge.flowRate * jitter),
      };
    }));
  }, []);

  // Advance a random truck's loading progress
  const advanceTruck = useCallback(() => {
    setTrucks(prev => {
      const loadingTrucks = prev.filter(t => t.status === 'loading' && t.currentWeight < t.targetWeight);
      if (loadingTrucks.length === 0) return prev;
      const truck = loadingTrucks[Math.floor(Math.random() * loadingTrucks.length)];
      return prev.map(t => {
        if (t.id !== truck.id) return t;
        const increment = Math.round((t.targetWeight * 0.05 + Math.random() * 2) * 10) / 10;
        const newWeight = Math.min(t.currentWeight + increment, t.targetWeight);
        return { ...t, currentWeight: newWeight };
      });
    });
  }, []);

  // Toggle a random alert dismissed state
  const cycleAlert = useCallback(() => {
    setDashboard(prev => {
      const undismissed = prev.alerts.filter(a => !a.dismissed && a.type !== 'success');
      if (undismissed.length === 0) return prev;
      const target = undismissed[Math.floor(Math.random() * undismissed.length)];
      return {
        ...prev,
        alerts: prev.alerts.map(a => a.id === target.id ? { ...a, dismissed: true } : a),
      };
    });
  }, []);

  useEffect(() => {
    // Jitter every 5 seconds
    const jitterInterval = setInterval(() => {
      jitterNodes();
      setTick(t => t + 1);
    }, 5000);

    // Advance truck every 15 seconds
    const truckInterval = setInterval(advanceTruck, 15000);

    // Cycle alerts every 30 seconds
    const alertInterval = setInterval(cycleAlert, 30000);

    return () => {
      clearInterval(jitterInterval);
      clearInterval(truckInterval);
      clearInterval(alertInterval);
    };
  }, [jitterNodes, advanceTruck, cycleAlert]);

  // Dismiss alert manually
  const dismissAlert = useCallback((alertId) => {
    setDashboard(prev => ({
      ...prev,
      alerts: prev.alerts.map(a => a.id === alertId ? { ...a, dismissed: true } : a),
    }));
  }, []);

  return {
    // Live data (changes over time)
    nodes,
    edges,
    trucks,
    dashboard,
    weighBridge,
    tick,
    // Actions
    dismissAlert,
    // Static data (doesn't change)
    orders,
    loadingSlots,
    planningData,
    materialColors,
    // Re-exported helpers
    getOrderById,
    getTruckById,
    getNodeById,
    getOrdersForNode,
    getSlotForTruck,
    getTrucksForGate,
    getOrdersByStatus,
    orderStatuses,
    truckStatuses,
    orderStatusLabels,
    truckStatusLabels,
  };
}
